/**
 * Local {@link IOspAuthModule} security / storage / refresh (SDK-free for these paths).
 * Uses the same localStorage keys as SDK AuthModule.
 */

import type {
  IOspCredentials,
  IOspCredentialsStorage,
  IOspLoginInfo,
} from "@features/auth/compat/sdk-types";
import { LoginType } from "@features/auth/compat/sdk-types";
import { authCacheManager } from "@features/auth/compat/auth-cache-compat";

type IOspAccessToken = { access_token?: string; refresh_token?: string };
type IOspIdToken = { id_token?: string };

import { getAuthIdToken, postAuthLogin, postAuthRefresh } from "./authApi";
import { getAccessTokenExpTimestamp, OspCredentials } from "./credentials";
import { ID_TOKEN_CACHE_BUFFER_MS } from "./constants";
import type { OspAuthApiConfig } from "./types";
import {
  decodeAccessTokenLoginJwt,
  isJwtAudMatchingApp,
  mapJwtToLoginInfo,
} from "./loginInfo";
import {
  getBrowserOspStorageProvider,
  OspCredentialsStorage,
  type OspStorageProvider,
} from "./credentialsStorage";

function toIoCredentialsStorage(
  storage: OspCredentialsStorage,
): IOspCredentialsStorage {
  return {
    getCredentials: async (): Promise<IOspCredentials | null> => {
      const credentials = await storage.getCredentials();
      if (!credentials) {
        return null;
      }
      return {
        accessToken: credentials.accessToken,
        refreshToken: credentials.refreshToken,
      };
    },
    setCredentials: async (credentials: IOspCredentials): Promise<void> => {
      await storage.setCredentials(
        new OspCredentials(credentials.accessToken, credentials.refreshToken),
      );
    },
    setIdToken: (idToken: string) => storage.setIdToken(idToken),
    reset: () => storage.reset(),
  };
}

export interface OspSecurityWorkerConfig extends OspAuthApiConfig {
  storageProvider?: OspStorageProvider;
  storageBaseUrl?: string;
}

interface OspSecureRequestParams {
  headers?: Record<string, string>;
  [key: string]: unknown;
}

export interface OspSecurityWorker {
  /** Same keys as SDK `CredentialsStorage` (for {@link IOspAuthModule.storage}). */
  storage: IOspCredentialsStorage;
  authenticate: (
    eoa_address: string,
    signature: string,
    options?: Record<string, string> & { issue_platform?: string },
  ) => Promise<IOspAccessToken>;
  idToken: () => Promise<IOspIdToken>;
  getLoginInfo: () => Promise<IOspLoginInfo | null | undefined>;
  getRequestHeader: () => Promise<Record<string, string>>;
  getSecurityData: (
    secureData?: unknown,
  ) => Promise<OspSecureRequestParams | void>;
  refreshCredentials: (
    check?: boolean,
    expTimeMs?: number,
  ) => Promise<OspCredentials | null>;
  logout: () => Promise<void>;
}

export function createOspSecurityWorker(
  config: OspSecurityWorkerConfig,
): OspSecurityWorker {
  const credentialsStorage = new OspCredentialsStorage(
    config.storageProvider ?? getBrowserOspStorageProvider(),
    config.storageBaseUrl ?? config.indexBaseUrl,
  );
  const storage = toIoCredentialsStorage(credentialsStorage);
  const logout = async (): Promise<void> => {
    await credentialsStorage.reset();
  };

  const refreshCredentials = async (
    check = false,
    expTimeMs?: number,
  ): Promise<OspCredentials | null> => {
    let credentials = await credentialsStorage.getCredentials();
    if (!credentials) {
      return null;
    }
    if (
      !check ||
      (credentials.shouldRefresh(expTimeMs) && credentials.canRefresh())
    ) {
      try {
        const tokens = await postAuthRefresh(config, credentials.refreshToken!);
        credentials = OspCredentials.fromApiTokens(tokens);
        await credentialsStorage.setCredentials(credentials);
      } catch {
        await logout();
        return null;
      }
    }
    return credentials;
  };

  const authenticate = async (
    eoa_address: string,
    signature: string,
    options?: Record<string, string> & { issue_platform?: string },
  ): Promise<IOspAccessToken> => {
    const tokens = await postAuthLogin(config, eoa_address, signature, options);
    await credentialsStorage.setCredentials(
      OspCredentials.fromApiTokens(tokens),
    );
    return tokens;
  };

  const idToken = async (): Promise<IOspIdToken> => {
    const cached = await credentialsStorage.getIdToken();
    if (
      cached &&
      getAccessTokenExpTimestamp(cached) > Date.now() + ID_TOKEN_CACHE_BUFFER_MS
    ) {
      return { id_token: cached };
    }
    const credentials = await refreshCredentials(true);
    if (!credentials?.accessToken) {
      return {};
    }
    const result = await getAuthIdToken(config, credentials.accessToken);
    if (result.id_token) {
      await credentialsStorage.setIdToken(result.id_token);
    }
    return result;
  };

  const getLoginInfo = async (): Promise<IOspLoginInfo | null | undefined> => {
    const credentials = await credentialsStorage.getCredentials();
    if (!credentials?.accessToken) {
      return null;
    }
    const ospCache = authCacheManager.getOspLoginCache();
    const isEmailSession = ospCache?.loginType === LoginType.OSP_EMAIL;

    try {
      let payload = decodeAccessTokenLoginJwt(credentials.accessToken);
      if (!isJwtAudMatchingApp(payload.aud, config.appId)) {
        if (isEmailSession) {
          return ospCache?.loginInfo ?? null;
        }
        await logout();
        return null;
      }
      if (!payload.chains?.length) {
        if (isEmailSession) {
          return ospCache?.loginInfo ?? null;
        }
        await refreshCredentials();
        const refreshed = await credentialsStorage.getCredentials();
        if (!refreshed?.accessToken) {
          return null;
        }
        payload = decodeAccessTokenLoginJwt(refreshed.accessToken);
      }
      return mapJwtToLoginInfo(payload);
    } catch {
      if (isEmailSession) {
        return ospCache?.loginInfo ?? null;
      }
      await logout();
      return null;
    }
  };

  /**
   * 鉴权头仅含 Bearer。`OS-Reg-Brand` 由各 ospprotocol Index 客户端的
   * `baseApiParams` 注入，勿放在此处 —— Chat/perplexica 共用本 securityWorker，
   * 带上该头会触发 CORS preflight 失败。
   */
  const getRequestHeader = async (): Promise<Record<string, string>> => {
    const credentials = await refreshCredentials(true);
    if (credentials?.accessToken) {
      return {
        authorization: `Bearer ${credentials.accessToken}`,
      };
    }
    return {};
  };

  const getSecurityData = async (
    secureData?: unknown,
  ): Promise<OspSecureRequestParams | void> => {
    const data =
      secureData && typeof secureData === "object"
        ? (secureData as OspSecureRequestParams)
        : {};
    data.headers = await getRequestHeader();
    return data;
  };

  return {
    storage,
    authenticate,
    idToken,
    getLoginInfo,
    getRequestHeader,
    getSecurityData,
    refreshCredentials,
    logout,
  };
}
